import { AdityaGroupMark, AeonixMark, ShyamSteelMark, WiproMark } from "../assets";
import { experience } from "../content";
import type { ExperienceEntry } from "../types/content";

/**
 * ============================================================================
 * Experience id → company mark. Presentation, keyed by content-layer id.
 * ============================================================================
 *
 * The twin of `educationLogos.ts`, and deliberately so: the two spines sit on
 * one page, share `.logo-plate`, and are read together. What differs is the
 * SECOND assertion, and the difference is real rather than an oversight — see
 * below.
 *
 * WHY THIS IS NOT A FIELD IN `experience.json`. The layer holds facts about the
 * role; which picture represents a company is a presentation asset. The schema
 * has never carried an image for an experience entry, and `workshopImages.ts`,
 * `nonCredlyCerts.ts` and `educationLogos.ts` are three precedents for keying a
 * display asset off the layer's id rather than putting a bundler import inside
 * a JSON file.
 *
 * ORPHANS THROW, THE WAY THEY DO FOR EDUCATION. A key naming an id the layer
 * does not carry is a typo or a retired role, and it renders nowhere: the node
 * silently falls back to initials and looks like a design choice. D-066 records
 * a gate written to check exactly this that parsed a JSDoc example instead of
 * the declaration, so the check that cannot be fooled that way is the one the
 * module runs on ITSELF, at load, against the real imports. `prerender.mjs`
 * executes the component tree, so the throw fails the BUILD.
 *
 * AN UNMARKED ROLE DOES NOT THROW, AND THAT IS THE ONE DELIBERATE DIVERGENCE
 * FROM `educationLogos.ts`. That module refuses absence because all four crests
 * exist and "a map that tolerates absence cannot tell a missing crest from a
 * new one". Here all four marks exist today too — so the same rule would
 * compile — but `Experience_v2` has carried an initials fallback since D-038,
 * it is REACHABLE state rather than dead code, and the brief that supplied
 * these four named it as the required behaviour for a company whose logo does
 * not arrive. Turning that fallback into a build failure would be a UI change
 * under Article II.5 with no defect behind it, and it would mean the next role
 * the owner adds cannot ship until a logo is sourced for it.
 *
 * So the invariant this module actually defends is the weaker, true one: every
 * key names a real role, and `companyMark` returns `undefined` rather than a
 * broken path when a role has no mark. COVERAGE IS THEREFORE NOT GUARANTEED BY
 * THE TYPE — it is asserted from outside, against the built HTML, by
 * `logos.mjs`, which counts plates and initials and requires the two to account
 * for every role on the spine. That is D-065's rule: the gate here answers "may
 * this be shown?", and only a check against the emitted page answers "is it?".
 */
const MARKS: Readonly<Record<string, string>> = {
	"shyam-steel-2024": ShyamSteelMark,
	"aeonix-2020": AeonixMark,
	"wipro-2019": WiproMark,
	"aditya-group-2018": AdityaGroupMark
};

const layerIds: string[] = experience.map((entry: ExperienceEntry) => entry.id);
const orphans: string[] = Object.keys(MARKS).filter((id: string) => !layerIds.includes(id));
if (orphans.length) {
	throw new Error(
		`companyLogos: ${orphans.join(", ")} name no entry in experience.json. A mark keyed to an id the layer ` +
			"does not carry renders nowhere, and the spine would fall back to initials as though that were the design."
	);
}

/** The company's mark for a content-layer experience id, or undefined — initials are the fallback. */
export const companyMark = (id: string): string | undefined => MARKS[id];

/** Ids carrying a mark, for the join gate to check against the built page. */
export const markedCompanyIds: string[] = Object.keys(MARKS);
